import { Phone, MapPin, CheckCircle, Clock, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { SEOHead } from "../SEOHead";
import { Header } from "../Header";
import { Footer } from "../Footer";
import { FloatingContactButtons } from "../FloatingContactButtons";
import { companyInfo } from "../../data/mock";

export const BethnalGreenPage = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      <SEOHead 
        title="Car Recovery Bethnal Green | Vehicle Transport E2 | 24/7 Breakdown Service"
        description="Fast car recovery in Bethnal Green E2. 30-minute response. 24/7 breakdown service, vehicle transport, towing. Call 07492 639223 for Bethnal Green car recovery."
        keywords="car recovery bethnal green, breakdown service bethnal green, car recovery e2, bethnal green vehicle transport, bethnal green towing, cambridge heath car recovery"
      />
      <Header />

      <section className="bg-gradient-to-br from-rose-900 via-pink-800 to-red-900 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-pink-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              🚗 Local Bethnal Green Service
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Car Recovery & Vehicle Transport in Bethnal Green
            </h1>
            <p className="text-xl text-rose-100 mb-8">
              Fast 30-minute car recovery across Bethnal Green E2. Available 24/7 for breakdowns, vehicle transport, and emergency recovery in Bethnal Green, Cambridge Heath, and surrounding areas.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={`tel:${companyInfo.phone}`}>
                <Button size="lg" className="bg-white text-rose-900 hover:bg-slate-100 text-lg px-8 py-6">
                  <Phone className="w-5 h-5 mr-2" />
                  Call: {companyInfo.phone}
                </Button>
              </a>
              <Link to="/#quote">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-rose-900 text-lg px-8 py-6">
                  Get Quote
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-8 bg-white border-b">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            <div className="text-center">
              <p className="text-3xl font-bold text-rose-600">30 Min</p>
              <p className="text-sm text-slate-600">Avg Response</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-rose-600">24/7</p>
              <p className="text-sm text-slate-600">Always Available</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-rose-600">E2</p>
              <p className="text-sm text-slate-600">Full Coverage</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-rose-600">Local</p>
              <p className="text-sm text-slate-600">East London</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">
              Professional Car Recovery in Bethnal Green
            </h2>
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              Ovawell Autos provides fast, professional car recovery and vehicle transport services across Bethnal Green E2. Our local team knows every street from Cambridge Heath Road to Roman Road and delivers an average 30-minute response time for all breakdown and emergency recovery situations.
            </p>

            <h3 className="text-2xl font-bold text-slate-900 mb-4 mt-8">
              Bethnal Green Areas We Serve
            </h3>
            <div className="grid md:grid-cols-3 gap-4 mb-8">
              {[
                "Bethnal Green E2",
                "Cambridge Heath E2",
                "Globe Town E2",
                "Roman Road E2",
                "Bethnal Green Road",
                "Cambridge Heath Road",
                "Roman Road Market",
                "Paradise Row",
                "Victoria Park Village",
                "Columbia Road",
                "Haggerston E2",
                "Museum Gardens"
              ].map((area, index) => (
                <div key={index} className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-rose-600 flex-shrink-0" />
                  <span className="text-slate-700">{area}</span>
                </div>
              ))}
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mb-4 mt-8">
              Our Bethnal Green Recovery Services
            </h3>
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              {[
                "24/7 breakdown recovery",
                "Vehicle transportation",
                "Emergency roadside assistance",
                "Jump start and battery boost",
                "Accident recovery",
                "Flat tyre recovery",
                "Out of fuel delivery",
                "Non-running vehicle transport",
                "Keys locked in car",
                "Car auction transport",
                "Urgent towing service",
                "Cambridge Heath station recovery"
              ].map((item, index) => (
                <div key={index} className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-rose-600 flex-shrink-0 mt-1" />
                  <span className="text-slate-700">{item}</span>
                </div>
              ))}
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mb-4 mt-8">
              Why Choose Ovawell Autos in Bethnal Green?
            </h3>
            <div className="space-y-4 mb-8">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <Clock className="w-8 h-8 text-rose-600 flex-shrink-0" />
                    <div>
                      <h4 className="font-bold text-lg text-slate-900 mb-2">Fast Local Response - 30 Minutes</h4>
                      <p className="text-slate-600">
                        Based in East London, we reach Bethnal Green within 30 minutes on average. We know all the residential streets, market areas, and busy roads in E2.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <MapPin className="w-8 h-8 text-rose-600 flex-shrink-0" />
                    <div>
                      <h4 className="font-bold text-lg text-slate-900 mb-2">We Know Bethnal Green Streets</h4>
                      <p className="text-slate-600">
                        From busy Bethnal Green Road to quiet residential streets, from Roman Road Market to Cambridge Heath station - we navigate all areas efficiently.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <CheckCircle className="w-8 h-8 text-rose-600 flex-shrink-0" />
                    <div>
                      <h4 className="font-bold text-lg text-slate-900 mb-2">24/7 Service - Transparent Pricing</h4>
                      <p className="text-slate-600">
                        Available every day including weekends and bank holidays. Fixed pricing with no hidden fees or night surcharges. Get a quote before we dispatch.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mb-4 mt-8">
              Common Breakdown Locations in Bethnal Green
            </h3>
            <ul className="list-disc list-inside space-y-2 text-slate-700 mb-8">
              <li><strong>Bethnal Green Road (A1209):</strong> Major route - priority response</li>
              <li><strong>Roman Road:</strong> Busy shopping area with market days</li>
              <li><strong>Cambridge Heath Road:</strong> Main thoroughfare</li>
              <li><strong>Residential streets off Globe Road:</strong> Quiet areas</li>
              <li><strong>Victoria Park borders:</strong> Park edge recovery</li>
              <li><strong>Cambridge Heath Station area:</strong> Station drop-off zone</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="bg-gradient-to-br from-rose-900 to-pink-900 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Broken Down in Bethnal Green? Call Now
          </h2>
          <p className="text-xl text-rose-100 mb-8 max-w-2xl mx-auto">
            24/7 car recovery and vehicle transport service across Bethnal Green E2 and surrounding areas.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`tel:${companyInfo.phone}`}>
              <Button size="lg" className="bg-white text-rose-900 hover:bg-slate-100 text-lg px-8 py-6">
                <Phone className="w-5 h-5 mr-2" />
                Call {companyInfo.phone}
              </Button>
            </a>
            <Link to="/#quote">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-rose-900 text-lg px-8 py-6">
                Get Quote Now
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingContactButtons />
    </div>
  );
};
